import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: ReactNode;
  /** Placement hint — tooltip flips if it would clip the viewport. */
  placement?: "top" | "bottom";
  /** Wrap a single element or render prop as the trigger. */
  children: ReactNode;
  /** Add extra class to the bubble. */
  className?: string;
}

/**
 * Hover tooltip rendered via portal to escape overflow:auto clipping.
 * Shows after a short delay; hides instantly on leave.
 */
export function Tooltip({
  content,
  placement = "top",
  children,
  className,
}: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{
    x: number;
    y: number;
    above: boolean;
  } | null>(null);
  const timerRef = useRef<number | null>(null);

  const show = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setVisible(true), 180);
  }, []);

  const hide = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setVisible(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!visible) {
      setPos(null);
      return;
    }
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;

    const tRect = trigger.getBoundingClientRect();
    const bRect = bubble.getBoundingClientRect();
    const spaceAbove = tRect.top;
    const spaceBelow = window.innerHeight - tRect.bottom;
    const wantAbove = placement === "top";
    const above =
      wantAbove && spaceAbove > bRect.height + 8
        ? true
        : !wantAbove && spaceBelow < bRect.height + 8
        ? true
        : wantAbove;
    const finalAbove =
      above && spaceAbove > bRect.height + 8
        ? true
        : spaceBelow < bRect.height + 8 && spaceAbove > spaceBelow;

    const y = finalAbove
      ? tRect.top - bRect.height - 8
      : tRect.bottom + 8;
    let x = tRect.left + tRect.width / 2 - bRect.width / 2;
    x = Math.max(
      8,
      Math.min(window.innerWidth - bRect.width - 8, x),
    );
    setPos({ x, y, above: finalAbove });
  }, [visible, placement, content]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="tooltip-trigger"
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            ref={bubbleRef}
            className={`tooltip-bubble ${
              pos?.above ? "tooltip-bubble--above" : "tooltip-bubble--below"
            } ${className ?? ""}`}
            style={{
              top: pos?.y ?? -9999,
              left: pos?.x ?? -9999,
              opacity: pos ? 1 : 0,
            }}
            role="tooltip"
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
